import { DateTime } from "luxon"
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts"

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart.tsx"
import type { KPI } from "@/types/kpiTypes.ts"
import { numberFmt } from "@/util/numberFmt.ts"

export default function KPIChart({
  kpi,
  minHeight,
  includeTarget,
  variant,
}: {
  kpi: KPI
  minHeight: number
  includeTarget: boolean
  variant: "line" | "bar"
}) {
  const chartConfig = {
    actual: {
      label: "Actual",
      color: "var(--chart-1)",
    },
    target: {
      label: "Target",
      color: "var(--chart-2)",
    },
  } satisfies ChartConfig

  const data =
    kpi.values
      ?.filter((v) => v.type == "Actual")
      .toReversed()
      .map((v) => {
        const target = kpi.values?.find(
          (t) => t.type == "Target" && t.locationId == v.locationId && t.date == v.date,
        )

        return {
          date: DateTime.fromISO(v.date).toFormat("L/d"),
          actual: v.value,
          target: target ? target.value : 0,
        }
      }) || []

  if (variant == "line") {
    return (
      <div className="overflow-x-hidden">
        <ChartContainer className="w-full max-h-[375px]" config={chartConfig} style={{ minHeight }}>
          <LineChart data={data} margin={{ top: 25, left: -15, right: 20, bottom: 5 }}>
            <XAxis dataKey="date" minTickGap={20} tickLine={false} />
            <YAxis tickLine={false} tickFormatter={(v) => numberFmt(Number(v))} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            <CartesianGrid vertical={false} />
            {includeTarget && (
              <Line
                dot={false}
                type="monotone"
                dataKey="target"
                stroke="var(--color-target)"
                strokeWidth={1}
                strokeDasharray="5,5"
              />
            )}
            <Line
              dot={false}
              type="monotone"
              dataKey="actual"
              stroke="var(--color-actual)"
              strokeWidth={2}
            />
          </LineChart>
        </ChartContainer>
      </div>
    )
  } else if (variant == "bar") {
    return (
      <div className="overflow-x-hidden">
        <ChartContainer className="w-full max-h-[375px]" config={chartConfig} style={{ minHeight }}>
          <BarChart data={data} margin={{ top: 25, left: -10, right: 10 }}>
            <XAxis dataKey="date" minTickGap={20} tickLine={false} />
            <YAxis tickLine={false} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            <CartesianGrid vertical={false} />
            <Bar dataKey="actual" fill="var(--color-actual)" />
          </BarChart>
        </ChartContainer>
      </div>
    )
  }
}
